import { PUBLIC_API_HOST } from "$env/static/public";
import type { Message } from "$bindings/Message";
import { socketStatus } from "./stores";

interface WebSocketOptions {
  reconnectInterval: number;
  maxReconnectAttempts: number;
}

export type ConnectionStatus = 'CONNECTING' | 'OPEN' | 'CLOSING' | 'CLOSED' | 'NOT_CONNECTED';

type MessageHandler = (data: Message) => void;

class WebSocketClient {
  private url: string;
  private options: WebSocketOptions;
  private ws: WebSocket | null;
  private reconnectAttempts: number;
  private messageHandlers: Set<MessageHandler>;
  private lotUpdatedHandlers: Set<MessageHandler>;
  private queue: Message[];
  private channels: Set<string>;

  constructor(url: string, options: Partial<WebSocketOptions> = {}) {
    this.url = url;
    this.options = {
      reconnectInterval: 500,
      maxReconnectAttempts: 240,
      ...options
    };

    this.ws = null;
    this.reconnectAttempts = 0;
    this.messageHandlers = new Set();
    this.lotUpdatedHandlers = new Set();
    this.queue = [];
    this.channels = new Set();
    this.connect();
  }

  private connect() {
    if (this.ws) return;

    this.ws = new WebSocket(this.url);

    this.ws.onopen = () => {
      socketStatus.set(this.getConnectionStatus());
      console.log('Websocket connected');
      this.reconnectAttempts = 0;

      const queue = [...this.queue];
      this.queue = [];

      for (const msg of queue) {
        this.send(msg);
      }

      for (const chan of this.channels) {
        this.send({ subscribe: chan });
      }
    };

    this.ws.onmessage = (event: MessageEvent) => {
      const data = event.data;
      console.log('socket message', data);
      this.notifyHandlers(JSON.parse(data));
    };

    this.ws.onclose = () => {
      console.log('WebSocket disconnected');
      socketStatus.set(this.getConnectionStatus());
      this.ws = null;

      if (this.reconnectAttempts < this.options.maxReconnectAttempts) {
        this.reconnectAttempts++;
        setTimeout(() => this.connect(), this.options.reconnectInterval);
      }
    };

    this.ws.onerror = (event: Event) => {
      console.error('WebSocket error:', event);
      socketStatus.set(this.getConnectionStatus());
    };
  }

  public send(data: Message) {
    let shouldEnqueue = false;
    if ('subscribe' in data) {
      this.channels.add(data.subscribe);
    } else if ('unsubscribe' in data) {
      this.channels.delete(data.unsubscribe);
    } else {
      shouldEnqueue = true;
    }

    const msg = JSON.stringify(data);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {

      console.debug('send', data);
      this.ws.send(msg);
    } else if (shouldEnqueue) {
      console.debug('queue', data);
      this.queue.push(data);
    } else {
      console.warn('Cannot send message: No active WebSocket connection');
    }
  }

  public onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  public onLotUpdated(handler: MessageHandler): () => void {
    this.lotUpdatedHandlers.add(handler);
    return () => this.lotUpdatedHandlers.delete(handler);
  }

  public notifyHandlers(data: Message) {
    this.messageHandlers.forEach(handler => handler(data));
    if ('lotUpdated' in data) {
      this.lotUpdatedHandlers.forEach(handler => handler(data));
    }
  }

  public disconnect() {
    this.ws?.close();
    this.ws = null;
  }

  public getConnectionStatus(): ConnectionStatus {
    if (!this.ws) return 'NOT_CONNECTED';
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'CONNECTING';
      case WebSocket.OPEN:
        return 'OPEN';
      case WebSocket.CLOSING:
        return 'CLOSING';
      case WebSocket.CLOSED:
        return 'CLOSED';
      default:
        return 'NOT_CONNECTED';
    }
  }
}

let instance: WebSocketClient | null = null;

export const websocketClient = () => {
  if (instance == null) {
    const protocol = document.location.protocol == 'https:' ? 'wss' : 'ws';
    const host = PUBLIC_API_HOST.split('://')[1];
    const url = `${protocol}://${host}/v1/ws`;
    instance = new WebSocketClient(url);
  }
  return instance;
};
